import { oc } from "@orpc/contract";
import { z } from "zod";

const CompanyLocationSchema = z.object({
  id: z.string(),
  companyCode: z.string(),
  companyName: z.string().nullable(),
  divisionCode: z.string(),
  divisionName: z.string().nullable(),
  locationCode: z.string(),
  description: z.string(),
  customerNumber: z.string().nullable(),
  branchShortCode: z.string().nullable(),
  abbBranchCode: z.string().nullable(),
  taxProvince: z.string().nullable(),
  vdmBranchCode: z.string().nullable(),
  glAccountType1: z.string().nullable(),
  glAccountType2: z.string().nullable(),
  glAccountType3: z.string().nullable(),
  glAccountDebit: z.string().nullable(),
  glAccount849RejectedType1: z.string().nullable(),
  glAccount849RejectedType2: z.string().nullable(),
  glAccount849RejectedType3: z.string().nullable(),
});

const ListResponseSchema = z.object({
  locations: z.array(CompanyLocationSchema),
});

const UpdateInputSchema = z.object({
  id: z.string().uuid(),
  companyCode: z.string().optional(),
  companyName: z.string().nullable().optional(),
  divisionCode: z.string().optional(),
  divisionName: z.string().nullable().optional(),
  locationCode: z.string().optional(),
  description: z.string().optional(),
  customerNumber: z.string().nullable().optional(),
  branchShortCode: z.string().nullable().optional(),
  abbBranchCode: z.string().nullable().optional(),
  taxProvince: z.string().nullable().optional(),
  vdmBranchCode: z.string().nullable().optional(),
  glAccountType1: z.string().nullable().optional(),
  glAccountType2: z.string().nullable().optional(),
  glAccountType3: z.string().nullable().optional(),
  glAccountDebit: z.string().nullable().optional(),
  glAccount849RejectedType1: z.string().nullable().optional(),
  glAccount849RejectedType2: z.string().nullable().optional(),
  glAccount849RejectedType3: z.string().nullable().optional(),
});

const CreateInputSchema = z.object({
  companyCode: z.string(),
  companyName: z.string().nullable().optional(),
  divisionCode: z.string(),
  divisionName: z.string().nullable().optional(),
  locationCode: z.string(),
  description: z.string(),
  customerNumber: z.string().nullable().optional(),
  branchShortCode: z.string().nullable().optional(),
  abbBranchCode: z.string().nullable().optional(),
  taxProvince: z.string().nullable().optional(),
  vdmBranchCode: z.string().nullable().optional(),
  glAccountType1: z.string().nullable().optional(),
  glAccountType2: z.string().nullable().optional(),
  glAccountType3: z.string().nullable().optional(),
  glAccountDebit: z.string().nullable().optional(),
  glAccount849RejectedType1: z.string().nullable().optional(),
  glAccount849RejectedType2: z.string().nullable().optional(),
  glAccount849RejectedType3: z.string().nullable().optional(),
});

const DeleteInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Org-wide JE template defaults for GL posting Excel downloads. Stored in
 * org_module_config.config.shipAndDebit.sd_gl_je_template. Applies to every
 * division's JE Template sheet header (divisionName still comes from
 * sd_company_locations).
 */
const JeTemplateConfigSchema = z.object({
  transType: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  sourceCode: z.string(),
  // Per-division email overrides keyed by division key (companyCode +
  // divisionCode, e.g. "W+", "QO"). Empty object when none configured.
  userEmailByDivision: z.record(z.string(), z.string()),
});

const UpdateJeTemplateInputSchema = z.object({
  transType: z.string().max(50),
  userId: z.string().max(50),
  // Allows a single address or a semicolon-separated list — not strictly an email.
  userEmail: z.string().max(255),
  sourceCode: z.string().max(50),
  // Per-division email overrides; blank entries are dropped on save.
  userEmailByDivision: z.record(z.string(), z.string().max(255)).default({}),
});

/**
 * Per-division shipping province for NO-TAX VDMs, keyed by division key
 * (companyCode + divisionCode, e.g. "W+", "QE"). Stored in
 * org_module_config.config.shipAndDebit.sd_vdm_no_tax_province_by_division.
 */
const NoTaxProvincesSchema = z.object({
  provincesByDivision: z.record(z.string(), z.string()),
});

const UpdateNoTaxProvincesInputSchema = z.object({
  // Province codes (e.g. ON, QC, MB). Blank entries are dropped on save.
  provincesByDivision: z.record(z.string(), z.string().max(10)).default({}),
});

export const companyLocationsContracts = {
  list: oc.output(ListResponseSchema).route({ method: "GET" }),
  create: oc
    .input(CreateInputSchema)
    .output(CompanyLocationSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateInputSchema)
    .output(CompanyLocationSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
  getJeTemplate: oc.output(JeTemplateConfigSchema).route({ method: "GET" }),
  updateJeTemplate: oc
    .input(UpdateJeTemplateInputSchema)
    .output(JeTemplateConfigSchema)
    .route({ method: "POST" }),
  getNoTaxProvinces: oc.output(NoTaxProvincesSchema).route({ method: "GET" }),
  updateNoTaxProvinces: oc
    .input(UpdateNoTaxProvincesInputSchema)
    .output(NoTaxProvincesSchema)
    .route({ method: "POST" }),
} as const;
